// Inline text of a legal page: `**bold**`, and e-mail and `https://` addresses
// as links. Nothing else is markup, so a stray character in the copy renders as
// itself. No lookbehind (the build targets safari14).

import { Fragment, type JSX, type ReactNode } from "react";

const TOKEN = /(\*\*[^*]+\*\*|[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}|https:\/\/[^\s,;)]+)/g;

const LINK = "text-primary-fg underline underline-offset-2 hover:text-primary-fg-2";

/** A sentence-ending full stop is not part of the address. */
const splitTrailingDot = (url: string): [string, string] =>
  url.endsWith(".") ? [url.slice(0, -1), "."] : [url, ""];

const renderToken = (token: string, key: number): ReactNode => {
  if (token.startsWith("**") && token.endsWith("**") && token.length > 4) {
    return (
      <strong key={key} className="font-semibold text-ink">
        {renderInline(token.slice(2, -2))}
      </strong>
    );
  }
  if (token.startsWith("https://")) {
    const [href, rest] = splitTrailingDot(token);
    return (
      <Fragment key={key}>
        <a href={href} className={LINK} rel="noreferrer">
          {href.replace("https://", "")}
        </a>
        {rest}
      </Fragment>
    );
  }
  if (token.includes("@")) {
    return (
      <a key={key} href={`mailto:${token}`} className={LINK}>
        {token}
      </a>
    );
  }
  return <Fragment key={key}>{token}</Fragment>;
};

export function renderInline(text: string): ReactNode[] {
  // `split` with one capture group alternates plain text and tokens.
  return text.split(TOKEN).map((part, i) =>
    i % 2 === 1 ? renderToken(part, i) : <Fragment key={i}>{part}</Fragment>,
  );
}

export function Inline({ text }: { readonly text: string }): JSX.Element {
  return <>{renderInline(text)}</>;
}
